import React, { useState } from "react";
import { TableHeaderButton } from "@/components/common/TableHeaderButton";
import TableSortingIcon from "@/components/common/TableSortingIcon";
import { generateImageSrc } from "@/utils/helper";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  deleteCategoryRequest,
  submitCategoryRequest,
} from "@/store/slices/category.slice";
import { Category } from "@/types/models/category";
import { ColumnDef } from "@tanstack/react-table";
import { EllipsisVertical, Pencil, FolderOpen, Eye, Trash2, Package2 } from "lucide-react";
import { useAppDispatch } from "@/store/hooks";
import { Link, useParams, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { useConfirmation } from "@/components/common/ConfirmationDialog";
import { toast } from "sonner";

const getStatusColor = (status: string) => {
  switch (status) {
    case "ACTIVE":
      return "bg-green-500/20 text-green-600 hover:bg-green-500/20";
    case "DRAFT":
      return "bg-orange-500/20 text-orange-600 hover:bg-orange-500/20";
    case "ARCHIVED":
      return "bg-red-500/20 text-red-600 hover:bg-red-500/20";
    default:
      return "bg-gray-500/20 text-gray-600 hover:bg-gray-500/20";
  }
};

const formatStatus = (status: string) => {
  switch (status) {
    case "ACTIVE":
      return "Active";
    case "DRAFT":
      return "Draft";
    case "ARCHIVED":
      return "Archived";
    default:
      return status.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  }
};

const getDescriptionPreview = (rawDescription: unknown) => {
  const extractDeltaText = (delta: any) =>
    Array.isArray(delta?.ops)
      ? delta.ops.map((op: any) => (typeof op?.insert === "string" ? op.insert : "")).join("")
      : "";

  let plainText = "";

  if (rawDescription && typeof rawDescription === "object") {
    plainText = extractDeltaText(rawDescription);
  } else if (typeof rawDescription === "string") {
    const value = rawDescription.trim();
    if (value) {
      try {
        const parsed = JSON.parse(value);
        plainText = typeof parsed === "string" ? parsed : extractDeltaText(parsed);
      } catch {
        plainText = value;
      }
    }
  }

  return plainText.replace(/\s+/g, " ").trim();
};

export const getColumns = (): ColumnDef<Category>[] => [
  {
    accessorKey: "title",
    size: 280,
    minSize: 220,
    maxSize: 400,
    header: ({ column }: any) => {
      return (
        <Button
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          variant="ghost"
          className="w-full flex p-0 items-center justify-between hover:!bg-transparent hover:text-inherit group"
        >
          <span className="text-base font-bold">Collection</span>
          <div
            className={`transition-opacity duration-200 ${column.getIsSorted() ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}
          >
            <TableSortingIcon sortDirection={column.getIsSorted()} />
          </div>
        </Button>
      );
    },
    cell: ({ row }: any) => {
      const category = row.original;
      const imageAsset = category.image_asset;
      const imageField = category.imageAssetId || "";

      let imagePath = "";

      if (imageAsset?.path) {
        imagePath = imageAsset.path;
      } else if (imageField.startsWith("/store/")) {
        imagePath = imageField;
      }

      const imageSrc = imagePath ? generateImageSrc(imagePath, 60, 60) : "";

      const createdDate = category.created_at
        ? new Date(category.created_at)
        : new Date();
      const formattedDate = format(createdDate, "MMM dd yyyy");

      return (
        <div className="flex gap-3 items-center">
          <div className="w-10 h-10 overflow-hidden object-cover rounded-md flex-shrink-0">
            {imageSrc ? (
              <img
                src={imageSrc}
                alt={category.title}
                className="w-10 h-10 object-cover"
                onError={(e) => {
                  const el = e.currentTarget as HTMLImageElement;
                  el.style.display = "none";
                }}
              />
            ) : (
              <div className="w-10 h-10 bg-muted rounded-md flex items-center justify-center">
                <FolderOpen className="h-4 w-4 text-muted-foreground" />
              </div>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <h3
              className="text-sm font-semibold truncate max-w-[200px]"
              title={category.title}
            >
              {category.title}
            </h3>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-xs text-muted-foreground">
                {formattedDate}
              </span>
              {category.slug && (
                <>
                  <span className="text-xs text-muted-foreground">•</span>
                  <span className="text-xs text-muted-foreground font-mono">
                    {category.slug}
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
      );
    },
  },
  {
    accessorKey: "products",
    size: 140,
    minSize: 100,
    maxSize: 180,
    accessorFn: (row: any) => row.total_products !== undefined ? row.total_products : (row.products?.length || 0),
    header: ({ column }: any) => {
      return (
        <div className="group">
          <Button
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            variant="ghost"
            className="w-full flex p-0 items-center justify-between hover:!bg-transparent hover:text-inherit"
          >
            <span className="text-base font-bold">Products</span>
            <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
              <TableSortingIcon sortDirection={column.getIsSorted()} />
            </div>
          </Button>
        </div>
      );
    },
    cell: ({ row }: any) => {
      const category = row.original;
      const productCount = category.total_products !== undefined
        ? category.total_products
        : (category.products?.length || 0);
      return (
        <div>
          <h3 className="text-sm font-semibold text-blue-600">{productCount}</h3>
          <span className="text-xs text-gray-600">
            {productCount === 1 ? "product" : "products"}
          </span>
        </div>
      );
    },
  },
  {
    accessorKey: "description",
    size: 200,
    minSize: 150,
    maxSize: 300,
    header: () => <span className="text-base font-bold">Description</span>,
    cell: ({ row }) => {
      const description = getDescriptionPreview(row.original.description);
      const truncated = description.length > 45 ? `${description.substring(0, 45)}...` : description;

      return (
        <div>
          <p
            className="text-xs text-gray-700 truncate max-w-[180px]"
            title={description}
          >
            {truncated || <span className="text-gray-400">No description</span>}
          </p>
        </div>
      );
    },
  },
  {
    accessorKey: "updated_at",
    size: 140,
    minSize: 120,
    maxSize: 180,
    header: ({ column }: any) => {
      return (
        <div className="group">
          <Button
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            variant="ghost"
            className="w-full flex p-0 items-center justify-between hover:!bg-transparent hover:text-inherit"
          >
            <span className="text-base font-bold">Updated</span>
            <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
              <TableSortingIcon sortDirection={column.getIsSorted()} />
            </div>
          </Button>
        </div>
      );
    },
    cell: ({ row }: any) => {
      const date = row.original.updated_at
        ? new Date(row.original.updated_at)
        : new Date();
      const formattedDate = format(date, "MMM dd yyyy");
      return (
        <div>
          <h3 className="text-xs font-semibold">{formattedDate}</h3>
        </div>
      );
    },
  },
  {
    accessorKey: "status",
    size: 120,
    minSize: 100,
    maxSize: 160,
    header: () => <span className="text-base font-bold">Status</span>,
    cell: ({ row }: any) => {
      const status = row.original.status || "DRAFT";
      return (
        <Badge
          variant="secondary"
          className={`rounded-full px-[10px] py-[5px] ${getStatusColor(status)}`}
        >
          {formatStatus(status)}
        </Badge>
      );
    },
  },
  {
    accessorKey: "actions",
    size: 220,
    minSize: 180,
    maxSize: 280,
    header: () => <span className="text-base font-bold">Actions</span>,
    cell: ({ row }: any) => {
      return <ActionTab row={row.original} />;
    },
  },
];

const ActionTab: React.FC<{ row: Category }> = ({ row }) => {
  const dispatch = useAppDispatch();
  const params = useParams();
  const navigate = useNavigate();
  const { confirm } = useConfirmation();
  const [isActive, setIsActive] = useState<boolean>(
    row.status === "ACTIVE",
  );

  const handleStatusToggle = () => {
    setIsActive((prev) => !prev);
    dispatch(
      submitCategoryRequest({
        collectionId: row.id,
        data: {
          metafields: row.metafields,
          title: row.title,
          description: row.description,
          status: isActive ? "DRAFT" : "ACTIVE",
        },
      }),
    );
  };

  const handleDelete = async () => {
    if (!row.id) return;

    const result = await confirm({
      title: "Delete Collection",
      message: `Are you sure you want to delete "${row.title}"? This action cannot be undone.`,
      confirmText: "Delete",
      cancelText: "Cancel",
      type: "destructive",
    });

    if (result.confirmed) {
      dispatch(
        deleteCategoryRequest({
          id: row.id,
        }),
      );
      toast.success("Collection deleted successfully");
    }
  };

  const handleViewProducts = () => {
    if (!params) return;
    // Navigate to products page filtered by this collection
    navigate(`/${params.storeType}/${params.storeId}/product?collection=${row.id}`);
  };

  if (!params) return null;

  return (
    <div className="flex items-center gap-3">
      <div className="flex items-center gap-2">
        <span className="text-xs text-gray-600">
          {row.status === "ACTIVE" ? "Active" : "Inactive"}
        </span>
        <Switch
          checked={isActive}
          onCheckedChange={handleStatusToggle}
        />
      </div>

      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0"
          onClick={handleViewProducts}
          title="View products in this collection"
        >
          <Package2 className="h-4 w-4" />
        </Button>

        <Link to={`/${params.storeType}/${params.storeId}/product/collection/create?q=${row.id}`}>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
            <Pencil className="h-4 w-4" />
          </Button>
        </Link>

        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
          <Eye className="h-4 w-4" />
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
              <EllipsisVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="bg-card dark:bg-card text-foreground"
            align="end"
          >
            <DropdownMenuLabel>Actions</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleViewProducts}>
              <Package2 className="h-4 w-4 mr-2" />
              View Products
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={handleDelete}
              className="text-red-600 focus:text-red-600"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete Collection
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};

// Named export
export const columns = getColumns;

// Default export
export default getColumns;
